import { Star, StarBorder } from "@mui/icons-material";
import ExitToApp from "@mui/icons-material/ExitToApp";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from "@mui/material";
import { Formik } from "formik";
import _ from "lodash";
import { ReactElement, useCallback, useState } from "react";
import { Form } from "react-router-dom";
import * as Yup from "yup";

import {
  ArchiveOwnedPhoneNumberPoolMutation,
  ArchiveOwnedPhoneNumberPoolMutationVariables,
  CreateOwnedPhoneNumberPoolMutation,
  CreateOwnedPhoneNumberPoolMutationVariables,
  EditCampaignMessagingStrategyBandwidthUnregisteredPoolMutation,
  EditSubaccountDefaultMessagingStrategyBandwidth10DlcPoolMutation,
  EditSubaccountDefaultMessagingStrategyBandwidth10DlcPoolMutationVariables,
  EditSubaccountDefaultMessagingStrategyBandwidthUnregisteredPoolMutationVariables,
  ListOwnedPhoneNumberPoolsQuery,
  OwnedPhoneNumberPoolType,
  UnarchiveOwnedPhoneNumberPoolMutation,
  UnarchiveOwnedPhoneNumberPoolMutationVariables,
} from "~frontend/__generated__/graphql";
import { CollapsibleContainer } from "~frontend/components/CollapsibleContainer";
import Table from "~frontend/components/Table";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import Archive from "~frontend/components/icons/Archive";
import Unarchive from "~frontend/components/icons/Unarchive";
import {
  ARCHIVE_OWNED_PHONE_NUMBER_POOL,
  CREATE_OWNED_PHONE_NUMBER_POOL,
  UNARCHIVE_OWNED_PHONE_NUMBER_POOL,
} from "~frontend/graphql/ownedPhoneNumberPool";
import {
  EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_10DLC_POOL,
  EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_UNREGISTERED_POOL,
  GET_SUBACCOUNT_ADMIN_FIELDS,
} from "~frontend/graphql/subaccounts";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { SubaccountAdminData } from "~frontend/providers/LoggedInUserProvider/context";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { NumberStatusTag } from "./PhoneNumberTable";

type OwnedPhoneNumberPool =
  ListOwnedPhoneNumberPoolsQuery["listOwnedPhoneNumberPools"][number];

const POOL_TYPE_DISPLAY_NAMES: { [K in OwnedPhoneNumberPoolType]: string } = {
  TEN_DLC_MMS: "10DLC MMS",
  BANDWIDTH_UNREGISTERED: "Unregistered",
  FAKE: "Fake",
};

function CreatePoolDialog({
  open,
  onClose,
  subaccountId,
}: {
  open: boolean;
  onClose: () => void;
  subaccountId: string;
}): ReactElement {
  const [createOwnedPhoneNumberPool] = useMutation<
    CreateOwnedPhoneNumberPoolMutation,
    CreateOwnedPhoneNumberPoolMutationVariables
  >(CREATE_OWNED_PHONE_NUMBER_POOL, {
    refetchQueries: ["ListOwnedPhoneNumberPools"],
  });

  const getErrorMessage = useErrorMessageFormatter();

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>Add Pool</DialogTitle>
      <Formik
        initialValues={{
          [submitErrorField]: null,
          name: "",
          type: "",
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().required(),
          type: Yup.string().required(),
        })}
        onSubmit={async (values, formik) => {
          const { errors } = await createOwnedPhoneNumberPool({
            variables: {
              input: {
                name: values.name,
                type: values.type as OwnedPhoneNumberPoolType,
                subaccountId,
              },
            },
          });

          if (!errors) {
            formik.resetForm();
            onClose();
          } else {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(errors),
            });
          }
        }}
      >
        {({ isSubmitting, handleSubmit }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner />

              <FormikTextField name="name" label="Name" fullWidth />

              <FormikSelect name="type" label="Type" fullWidth>
                {Object.values(OwnedPhoneNumberPoolType).map(type => (
                  <MenuItem key={type} value={type}>
                    {POOL_TYPE_DISPLAY_NAMES[type]}
                  </MenuItem>
                ))}
              </FormikSelect>
            </DialogContent>

            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="secondary"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                onClick={() => handleSubmit()}
                color="primary"
                variant="contained"
                submitText="Add"
                loadingText="Adding..."
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default function PhoneNumberPools({
  subaccountId,
  ownedPhoneNumberPools,
}: {
  subaccountId: string;
  ownedPhoneNumberPools: ListOwnedPhoneNumberPoolsQuery["listOwnedPhoneNumberPools"];
}): ReactElement | null {
  const isStaff = useCurrentUserIsStaff();
  const { subaccountLink } = useSubaccount()!;
  const { defaultMessagingStrategy } = useSubaccountAdminConfig()!;
  const client = useNonCacheClearingApolloClient();

  const [createPoolDialogOpen, setCreatePoolDialogOpen] = useState(false);

  const [setDefaultMessagingStrategyBandwidth10DlcPool] = useMutation<
    EditSubaccountDefaultMessagingStrategyBandwidth10DlcPoolMutation,
    EditSubaccountDefaultMessagingStrategyBandwidth10DlcPoolMutationVariables
  >(EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_10DLC_POOL, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client,
  });

  const [setDefaultMessagingStrategyBandwidthUnregisteredPool] = useMutation<
    EditCampaignMessagingStrategyBandwidthUnregisteredPoolMutation,
    EditSubaccountDefaultMessagingStrategyBandwidthUnregisteredPoolMutationVariables
  >(EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_UNREGISTERED_POOL, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client,
  });

  const setDefaultPool = useCallback(
    async (pool: OwnedPhoneNumberPool) => {
      if (pool.type === OwnedPhoneNumberPoolType.TenDlcMms) {
        await setDefaultMessagingStrategyBandwidth10DlcPool({
          variables: {
            input: {
              subaccountId,
              config: { ownedPhoneNumberPoolId: pool.id },
            },
          },
        });
      } else if (pool.type === OwnedPhoneNumberPoolType.BandwidthUnregistered) {
        await setDefaultMessagingStrategyBandwidthUnregisteredPool({
          variables: {
            input: {
              subaccountId,
              config: { ownedPhoneNumberPoolId: pool.id },
            },
          },
        });
      } else {
        throw new Error(`Unrecognized pool type: ${pool.type}`);
      }
    },
    [
      subaccountId,
      setDefaultMessagingStrategyBandwidth10DlcPool,
      setDefaultMessagingStrategyBandwidthUnregisteredPool,
    ]
  );

  const [archivePool] = useMutation<
    ArchiveOwnedPhoneNumberPoolMutation,
    ArchiveOwnedPhoneNumberPoolMutationVariables
  >(ARCHIVE_OWNED_PHONE_NUMBER_POOL, {
    onErrorSnackbarMessage: "Failed to archive phone number pool",
  });

  const [unarchivePool] = useMutation<
    UnarchiveOwnedPhoneNumberPoolMutation,
    UnarchiveOwnedPhoneNumberPoolMutationVariables
  >(UNARCHIVE_OWNED_PHONE_NUMBER_POOL, {
    onErrorSnackbarMessage: "Failed to unarchive phone number pool",
  });

  return (
    <CollapsibleContainer
      header="Phone Number Pools"
      headerActions={
        isStaff && (
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: 12 }}
            onClick={() => setCreatePoolDialogOpen(true)}
          >
            + New Pool
          </Button>
        )
      }
    >
      <Box sx={{ ml: 3 }}>
        <Table
          columns={[
            {
              key: "name",
              title: "Name",
              renderValue: row =>
                row.archivedAt ? (
                  <>
                    <Box sx={{ color: palette.darkGray, fontStyle: "italic" }}>
                      {row.name}
                    </Box>
                    <NumberStatusTag
                      backgroundColor={palette.lightGray}
                      color={palette.darkGray}
                    >
                      Archived
                    </NumberStatusTag>
                  </>
                ) : (
                  row.name
                ),
            },
            {
              key: "type",
              title: "Type",
              renderValue: row => POOL_TYPE_DISPLAY_NAMES[row.type],
            },
            {
              key: "createdAt",
              title: "Created At",
              renderValue: row => new Date(row.createdAt).toLocaleString(),
            },
          ]}
          rowActionsLabel="Details"
          rowActions={pool => [
            {
              id: "details",
              type: "link",
              icon: <ExitToApp />,
              tooltipLabel: "Details",
              href: row => subaccountLink(`/phone-numbers/pools/${row.id}`),
            },
            ...(pool.type !== OwnedPhoneNumberPoolType.Fake
              ? [
                  {
                    id: "make-phone-number-pool-default",
                    type: "button" as const,
                    confirm: {
                      title: "Set Phone Number as Default",
                      description: `Make ${pool.name} the default pool?`,
                      cancellationText: "Cancel",
                      confirmationText: "Confirm",
                    },
                    icon: poolIsDefault(defaultMessagingStrategy, pool) ? (
                      <Star />
                    ) : (
                      <StarBorder />
                    ),
                    tooltipLabel: "Set as Default",
                    onClick: () => setDefaultPool(pool),
                  },
                ]
              : []),
            ...(isStaff && !poolIsDefault(defaultMessagingStrategy, pool)
              ? [
                  pool.archivedAt
                    ? {
                        id: "unarchive",
                        type: "button" as const,
                        confirm: {
                          title: "Unarchive Phone Number Pool",
                          description: `Are you sure you want to unarchive the phone number pool ${pool.name}?`,
                          cancellationText: "Cancel",
                          confirmationText: "Confirm",
                        },
                        icon: <Unarchive />,
                        tooltipLabel: "Unarchive",
                        onClick: async () => {
                          await unarchivePool({
                            variables: {
                              input: {
                                subaccountId,
                                poolId: pool.id,
                              },
                            },
                          });
                        },
                      }
                    : {
                        id: "archive",
                        type: "button" as const,
                        confirm: {
                          title: "Archive Phone Number Pool",
                          description: `Are you sure you want to archive the phone number pool ${pool.name}?`,
                          cancellationText: "Cancel",
                          confirmationText: "Confirm",
                        },
                        icon: <Archive />,
                        tooltipLabel: "Archive",
                        onClick: async () => {
                          await archivePool({
                            variables: {
                              input: {
                                subaccountId,
                                poolId: pool.id,
                              },
                            },
                          });
                        },
                      },
                ]
              : []),
          ]}
          rows={_.sortBy(ownedPhoneNumberPools, p => -p.createdAt)}
          emptyState="No phone number pools"
          noFooter={true}
        />
      </Box>
      <CreatePoolDialog
        open={createPoolDialogOpen}
        onClose={() => setCreatePoolDialogOpen(false)}
        subaccountId={subaccountId}
      />
    </CollapsibleContainer>
  );
}

function poolIsDefault(
  defaultMessagingStrategy: SubaccountAdminData["defaultMessagingStrategy"],
  pool: OwnedPhoneNumberPool
): boolean {
  if (!defaultMessagingStrategy) {
    return false;
  }

  switch (defaultMessagingStrategy.__typename) {
    case "CampaignMessagingStrategyBandwidth10DlcPool":
    case "CampaignMessagingStrategyBandwidthUnregisteredPool":
    case "CampaignMessagingStrategyFakePool":
      return (
        pool.id === defaultMessagingStrategy.config.ownedPhoneNumberPool.id
      );
    case "CampaignMessagingStrategyBandwidth10Dlc":
    case "CampaignMessagingStrategyBandwidthShortCode":
    case "CampaignMessagingStrategyBandwidthTollFree":
    case "CampaignMessagingStrategyTwilioShortCode":
    case "CampaignMessagingStrategySinch10Dlc":
    case "CampaignMessagingStrategyFake":
    case "CampaignMessagingStrategyFakeShortCode":
    case "CampaignMessagingStrategyFake10Dlc":
    case "CampaignMessagingStrategyFakeTollFree":
      return false;
  }
}
